import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";
import { getNameDay } from "../data/czechNameDays";

const DAY_NAMES = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
const MONTH_NAMES = [
  "ledna", "února", "března", "dubna", "května", "června",
  "července", "srpna", "září", "října", "listopadu", "prosince"
];

function formatDate(dateStr) {
  // dateStr: "YYYY-MM-DD"
  const parts = dateStr.split("-");
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const dayName = DAY_NAMES[d.getDay()];
  return `${dayName} ${d.getDate()}. ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

export default function WelcomeAdmin({ isDarkMode, userId }) {
  const [days, setDays] = useState([]); // [{ date: "YYYY-MM-DD", message: "" }]
  const [saving, setSaving] = useState({}); // { "YYYY-MM-DD": true/false }
  const [saved, setSaved] = useState({}); // { "YYYY-MM-DD": true } – potvrzení uložení
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/daily-messages?userId=${userId || ''}`);
      if (!res.ok) throw new Error("Chyba serveru");
      const data = await res.json();
      setDays(data);
    } catch (e) {
      setError("Nepodařilo se načíst zprávy: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (date, value) => {
    setDays(prev => prev.map(d => d.date === date ? { ...d, message: value } : d));
    // Zrušíme "uloženo" indikátor při editaci
    setSaved(prev => ({ ...prev, [date]: false }));
  };

  const handleSave = useCallback(async (date, message) => {
    setSaving(prev => ({ ...prev, [date]: true }));
    try {
      const res = await fetch(`${API_URL}/daily-messages?userId=${userId || ''}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, message, updatedBy: userId })
      });
      if (!res.ok) throw new Error("Chyba serveru");
      setSaved(prev => ({ ...prev, [date]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [date]: false })), 2500);
    } catch (e) {
      alert("Chyba při ukládání: " + e.message);
    } finally {
      setSaving(prev => ({ ...prev, [date]: false }));
    }
  }, [userId]);

  const handleKeyDown = (e, date, message) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSave(date, message);
    }
  };

  const filtered = search.trim()
    ? days.filter(d =>
        formatDate(d.date).toLowerCase().includes(search.toLowerCase()) ||
        d.message.toLowerCase().includes(search.toLowerCase()) ||
        getNameDay(d.date).toLowerCase().includes(search.toLowerCase())
      )
    : days;

  const base = isDarkMode
    ? "bg-gray-900 text-white"
    : "bg-gray-50 text-gray-900";

  const card = isDarkMode
    ? "bg-gray-800 border border-gray-700"
    : "bg-white border border-gray-200";

  return (
    <div className={`min-h-screen ${base} p-6`}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-1">📅 Uvítání – denní zprávy</h1>
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Ke každému dni napište citát nebo zprávu. Zobrazí se uživateli po přihlášení v daný den (jen jednou).
            Uložit: <kbd className="px-1 rounded border text-xs">Ctrl+Enter</kbd> nebo tlačítko Uložit.
          </p>
        </div>

        {/* Vyhledávání */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Hledat datum, jméno svátku nebo text..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg border text-sm ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
            }`}
          />
        </div>

        {loading && (
          <div className="text-center py-10 text-gray-400">Načítám...</div>
        )}
        {error && (
          <div className="text-red-500 py-4">{error}</div>
        )}

        {!loading && !error && (
          <div className="space-y-2">
            {filtered.map(({ date, message }) => {
              const nameDay = getNameDay(date);
              const isSaving = saving[date];
              const isSaved = saved[date];

              // Zvýraznění dnešního dne
              const todayStr = new Date().toISOString().split("T")[0];
              const isToday = date === todayStr;

              return (
                <div
                  key={date}
                  className={`${card} rounded-lg p-4 transition ${isToday ? "ring-2 ring-blue-500" : ""}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    {/* Datum a svátek */}
                    <div className="sm:w-56 flex-shrink-0">
                      <div className={`font-semibold text-sm ${isToday ? "text-blue-500" : ""}`}>
                        {isToday && <span className="mr-1">📍</span>}
                        {formatDate(date)}
                      </div>
                      {nameDay && (
                        <div className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          🎂 Svátek: {nameDay}
                        </div>
                      )}
                    </div>

                    {/* Textarea */}
                    <div className="flex-1 flex gap-2 items-start">
                      <textarea
                        rows={2}
                        value={message}
                        onChange={e => handleChange(date, e.target.value)}
                        onKeyDown={e => handleKeyDown(e, date, message)}
                        placeholder="Napište citát nebo zprávu pro tento den..."
                        className={`flex-1 px-3 py-2 rounded border text-sm resize-y ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                            : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400"
                        } focus:outline-none focus:ring-2 focus:ring-blue-400`}
                      />
                      <button
                        onClick={() => handleSave(date, message)}
                        disabled={isSaving}
                        className={`px-3 py-2 rounded text-sm font-semibold transition flex-shrink-0 ${
                          isSaved
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : "bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        }`}
                      >
                        {isSaving ? "..." : isSaved ? "✓" : "Uložit"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className={`text-center py-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                Žádné výsledky pro „{search}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
